import React, { useState } from 'react';
import type { LocalizedString } from '../../types';
import type { AdminDefaultOption, DefaultOptionInput } from '../../services/adminApi';
import LocalizedInput from './LocalizedInput';
import PriceInput from './PriceInput';
import { price_to_fields, fields_to_price } from './price';
import type { PriceFields } from './price';
import { EMPTY_LOCALIZED } from './localized';
import styles from './AdminCrud.module.css';

interface DefaultOptionFormProps {
  initial?: AdminDefaultOption;
  onSubmit: (input: DefaultOptionInput) => Promise<void>;
  onCancel: () => void;
}

/** Create/edit form for a default option (always included in the project) */
const DefaultOptionForm: React.FC<DefaultOptionFormProps> = ({
  initial,
  onSubmit,
  onCancel,
}) => {
  const [title, setTitle] = useState<LocalizedString>(initial?.title ?? EMPTY_LOCALIZED);
  const [description, setDescription] = useState<LocalizedString>(
    initial?.description ?? EMPTY_LOCALIZED
  );
  const [price, setPrice] = useState<PriceFields>(price_to_fields(initial?.price));
  const [days, setDays] = useState<string>(
    initial?.days_to_complete !== undefined ? String(initial.days_to_complete) : ''
  );
  const [sortOrder, setSortOrder] = useState<string>(String(initial?.sort_order ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handle_submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!title.ru.trim()) {
      setError('Укажите название на русском');
      return;
    }

    if (price.us.trim() === '' && (price.eu.trim() !== '' || price.kzt.trim() !== '')) {
      setError('Укажите цену в долларах — EUR/KZT считаются от неё');
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({
        title,
        description,
        price: fields_to_price(price),
        days_to_complete: days.trim() === '' ? null : Number(days),
        sort_order: Number(sortOrder) || 0,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to save default option');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className={styles.form_panel} onSubmit={handle_submit}>
      <LocalizedInput label="Title" field_key="title" value={title} onChange={setTitle} />
      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <LocalizedInput
          label="Description"
          field_key="description"
          value={description}
          onChange={setDescription}
          textarea
        />
      </div>

      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <PriceInput value={price} onChange={setPrice} />
      </div>

      <div className={styles.form_grid} style={{ marginTop: 'var(--spacing-md)' }}>
        <div className={styles.field}>
          <span className={styles.field_label}>Days to complete</span>
          <input
            className={styles.input}
            type="number"
            min={0}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.field_label}>Sort order</span>
          <input
            className={styles.input}
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
      </div>

      {error && <p className={styles.error_text}>{error}</p>}

      <div className={styles.actions_row}>
        <button type="submit" className={styles.button_primary} disabled={isSaving}>
          {isSaving ? 'Saving…' : initial ? 'Save' : 'Add default option'}
        </button>
        <button type="button" className={styles.button_secondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default DefaultOptionForm;
